import {
  nullishToOptional,
  PulsarContributionConfigurationProperty,
  PulsarContributionVariant,
  PulsarCustomBlock,
} from "@supernova-studio/model";
import { z } from "zod";
import { DTOExporterPropertyDefinition } from "./exporter-property";

//
// Read
//

export const DTOExporterType = z.enum(["documentation", "code"]);
export const DTOExporterSource = z.enum(["git", "upload"]);
export const DTOExporterMembershipRole = z.enum(["Owner", "OwnerArchived", "User"]);

export const DTOExporterListQuery = z.object({
  limit: z.coerce.number().optional(),
  offset: z.coerce.number().optional(),
  type: DTOExporterType.optional(),
  search: z.string().optional(),
});

export const DTOExporter = z.object({
  id: z.string(),
  name: z.string(),
  isPrivate: z.boolean(),
  exporterType: DTOExporterType,
  isDefaultDocumentationExporter: z.boolean(),

  iconURL: z.string().optional(),
  configurationProperties: PulsarContributionConfigurationProperty.array(),
  properties: DTOExporterPropertyDefinition.array().optional(),
  customBlocks: PulsarCustomBlock.array(),
  blockVariants: z.record(z.string(), PulsarContributionVariant.array()),

  homepage: z.string().optional(),
  organization: z.string().optional(),
  packageId: z.string().optional(),
  tags: z.array(z.string()),
  author: z.string().optional(),
  version: z.string(),
  description: z.string(),

  usesLocale: z.boolean(),
  usesBrands: z.boolean(),
  usesThemes: z.boolean(),

  readme: z.string().optional(),
  routingVersion: z.string().optional(),

  source: DTOExporterSource,

  gitProvider: z.string().optional(),
  gitUrl: nullishToOptional(z.string()),
  gitBranch: nullishToOptional(z.string()),
  gitDirectory: nullishToOptional(z.string()),

  isDeprecated: z.boolean(),
  deprecationNote: z.string().optional(),
  replacementExporterId: z.string().optional(),
});

export const DTOExporterMembership = z.object({
  workspaceId: z.string(),
  exporterId: z.string(),
  role: DTOExporterMembershipRole,
});

export const DTOExporterResponse = z.object({
  exporter: DTOExporter,
  membership: DTOExporterMembership,
});

export const DTOExporterListResponse = z.object({
  exporters: DTOExporter.array(),
  membership: DTOExporterMembership.array(),
  total: z.number(),
});

export type DTOExporterListQuery = z.infer<typeof DTOExporterListQuery>;
export type DTOExporterType = z.infer<typeof DTOExporterType>;
export type DTOExporterSource = z.infer<typeof DTOExporterSource>;
export type DTOExporterMembershipRole = z.infer<typeof DTOExporterMembershipRole>;
export type DTOExporter = z.infer<typeof DTOExporter>;
export type DTOExporterMembership = z.infer<typeof DTOExporterMembership>;
export type DTOExporterResponse = z.infer<typeof DTOExporterResponse>;
export type DTOExporterListResponse = z.infer<typeof DTOExporterListResponse>;

//
// Write
//

export const DTOExporterGitProviderEnum = z.enum(["github", "gitlab", "bitbucket", "azure"]);

export const DTOExporterCreateInput = z.object({
  url: z.string(),
  provider: DTOExporterGitProviderEnum,
});

export const DTOExporterUpdateInput = z.object({
  url: z.string().optional(),
});

export const DTOExporterDeprecationInput = z.object({
  isDeprecated: z.boolean(),
  deprecationNote: z.string().optional(),
  replacementExporterId: z.string().optional(),
});

export type DTOExporterGitProviderEnum = z.infer<typeof DTOExporterGitProviderEnum>;
export type DTOExporterCreateInput = z.infer<typeof DTOExporterCreateInput>;
export type DTOExporterUpdateInput = z.infer<typeof DTOExporterUpdateInput>;
export type DTOExporterDeprecationInput = z.infer<typeof DTOExporterDeprecationInput>;
